import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useToasts } from "react-toast-notifications";

export const useReconDetails = () => {
  const { addToast } = useToasts();
  const navigate = useNavigate();

  const { reconId } = useParams();
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const isMounted = useRef(true);

  const [transactionData, setTransactionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeOrderIndex, setActiveOrderIndex] = useState(0);
  const [activeMessageTab, setActiveMessageTab] = useState(0);

  // State for the new Issue Modal
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [toSubscriberId, setToSubscriberId] = useState("");
  const [toSubscriberUri, setToSubscriberUri] = useState("");

    const handleGoBack = () => {   
    navigate(-1);
  };

  useEffect(() => {
    isMounted.current = true;

    const fetchReconDetails = async () => {
      if (!reconId || !authData) return;

      setLoading(true);
      setError(null);

      try {
        const result = await apiHandler({
          url: endpoint.GET_RECONS_Detail + reconId,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        const data = result?.data?.data;

        if (isMounted.current && data) {
          setTransactionData(data);
          setActiveOrderIndex(0);
          setActiveMessageTab(0);
        }
      } catch (err) {
        if (isMounted.current) {
          //console.error("Error fetching recon details:", err);
          setError("Failed to fetch recon details.");
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    fetchReconDetails();

    return () => {
      isMounted.current = false;
    };
  }, [reconId, authData, dispatch]);

  const handleLatestSettlement = useCallback(
    async (e) => {
      e.preventDefault();

      if (!transactionData) {
        addToast("Transaction data not loaded yet.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const currentOrder = transactionData?.orders?.[activeOrderIndex];
      if (!currentOrder) {
        addToast("No order selected.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const {
        _id,
        buyer_app_id,
        buyer_app_uri,
        seller_app_id,
        seller_app_uri,
      } = currentOrder;

      if (
        !_id ||
        !buyer_app_id ||
        !buyer_app_uri ||
        !seller_app_id ||
        !seller_app_uri
      ) {
        //console.error("Invalid or incomplete order details", currentOrder);
        addToast(
          "Cannot send settlement request. Missing order or app details.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      try {
        setLoading(true);

        const url = `${endpoint.SEND_SETTLEMENT_REQUEST}`;

        const payload = {
          order_ids: [_id],
          app_details: {
            buyer_app_id,
            buyer_app_uri,
            seller_app_id,
            seller_app_uri,
          },
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const failedOrdersList =
          response?.data?.response?.data?.failedOrders?.filter(
            (item) => item && typeof item === "object" && item.orderId
          ) || [];

        let toastMessage =
          response?.data?.response?.data?.message ||
          "Settlement requested successfully";

        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Order:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          }
        );
      } catch (error) {
        //console.error("API Error:", error);

        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, transactionData, activeOrderIndex, addToast, storeId]
  );

  const handleRaiseRecon = useCallback(
    async (e) => {
      e.preventDefault();

      if (!transactionData) {
        addToast("Transaction data not loaded yet.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const currentOrder = transactionData?.orders?.[activeOrderIndex];
      if (!currentOrder) {
        addToast("No order selected.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const {
        _id,
        buyer_app_id,
        buyer_app_uri,
        seller_app_id,
        seller_app_uri,
      } = currentOrder;

      if (
        !_id ||
        !buyer_app_id ||
        !buyer_app_uri ||
        !seller_app_id ||
        !seller_app_uri
      ) {
        //console.error("Invalid or incomplete order details", currentOrder);
        addToast(
          "Cannot send settlement request. Missing order or app details.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      try {
        setLoading(true);
 
        const url = `${endpoint.RAISE_RECON}`;        
       
        const payload = {
          order_ids: [_id],
          app_details: {
            buyer_app_id,
            buyer_app_uri,
            seller_app_id,
            seller_app_uri,
          },
          ...(transactionData?.orders?.[activeOrderIndex]?.domain && { domain: transactionData.orders?.[activeOrderIndex]?.domain }),
        };
        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const failedOrdersList =
          response?.data?.response?.data?.failedOrders?.filter(
            (item) => item && typeof item === "object" && item.orderId
          ) || [];

        let toastMessage =
          response?.data?.response?.data?.message ||
          "Recon raised successfully";

        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Order:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          }
        );
      } catch (error) {
        //console.error("API Error:", error);

        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, transactionData, activeOrderIndex, addToast, storeId]
  );

  const handleRaiseIssueSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!paymentId || !toSubscriberId || !toSubscriberUri) {
        addToast("All fields are required.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }
      
      // if (!storeId) {
      //   addToast("Store ID (Tenant ID) not available. Cannot raise issue.", {
      //     appearance: "error",
      //     autoDismiss: true,
      //   });
      //   return;
      // }

      setIsSubmittingIssue(true);

      try {
        const url = `${endpoint.RAISE_RECON_ISSUE}?`;
        
        const payload = {
          tenant_id: storeId,
          payment_id: paymentId,
          to_subscriber_id: toSubscriberId,
          to_subscriber_uri: toSubscriberUri,
          domain: transactionData?.orders?.[activeOrderIndex]?.domain, // Use domain from transaction data or fallback to "default"
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        // --- MODIFIED SECTION ---
        const responseData = response?.data;
        //console.log("Raise Issue API Response:", responseData);
 
        if (responseData?.status === 200 && responseData?.redirectUrl) {
          // Success: We have a redirect URL.
          addToast("Issue raised. Redirecting to IGM...", {
            appearance: "success",
            autoDismiss: true,
          });

          // Open the redirect in a new tab
          window.open(responseData.redirectUrl, "_blank", "noopener,noreferrer");

          // Page is navigating away, no need to close modal or stop spinner
        } else {
          // Handle cases where the API call succeeded but didn't return
          // a redirectUrl, or returned a different status in its body.
          const toastMessage =
            responseData?.message ||
            "Issue raised successfully, but no redirect was provided.";

          addToast(toastMessage, {
            appearance: responseData?.status === 200 ? "success" : "error",
            autoDismiss: true,
          });

          if (responseData?.status === 200) {
            setShowIssueModal(false); // Close modal only on non-redirect success
          }
        }
        // --- END OF MODIFIED SECTION ---
      } catch (error) {
        //console.error("API Error raising issue:", error);

        // This catch block handles network errors or HTTP 4xx/5xx responses
        addToast(
          error.response?.data?.message ||
            "Unexpected error occurred while raising issue.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        // This will only be executed if no redirect happens
        setIsSubmittingIssue(false);
      }
    },
    [
      paymentId,
      toSubscriberId,
      toSubscriberUri,
      authData,
      dispatch,
      storeId,
      addToast,
    ]
  );

  // New handlers for opening/closing the modal
  const handleOpenIssueModal = useCallback(() => {
    const currentOrder = transactionData?.orders?.[activeOrderIndex];
    if (currentOrder) {
      // Pre-populate fields based on the current order
      setToSubscriberId(currentOrder.buyer_app_id || "");
      setToSubscriberUri(currentOrder.buyer_app_uri || "");
    }
    setPaymentId(""); // Clear payment ID for user input
    setShowIssueModal(true);
  }, [transactionData, activeOrderIndex]);

  const handleCloseIssueModal = () => {
    setShowIssueModal(false);
  };

  return {
    transactionData,
    loading,
    error,
    activeTab,
    activeOrderIndex,
    activeMessageTab,
    handleLatestSettlement,
    handleTabClick: setActiveTab,
    handleOrderClick: (index) => {
      setActiveOrderIndex(index);
      setActiveMessageTab(0);
    },
    handleMessageTabClick: setActiveMessageTab,
    handleRaiseRecon,

    // Export new modal properties
    showIssueModal,
    isSubmittingIssue,
    paymentId,
    setPaymentId,
    toSubscriberId,
    setToSubscriberId,
    toSubscriberUri,
    setToSubscriberUri,
    handleOpenIssueModal,
    handleCloseIssueModal,
    handleRaiseIssueSubmit,
    handleGoBack
  };
};
